import type { _ReactPixi } from '@inlet/react-pixi';
import type { PropsWithChildren } from 'lib-shared-types-utils';
import type { IBitmapTextStyle } from 'pixi.js';
import {
  Children,
  cloneElement,
  createElement,
  Fragment,
  ReactElement,
  useMemo,
} from 'react';


import { styles as nodeStyles } from '../renderers/node/nodeStyles';
import type { BaseElementProps } from '../types/text';
import type { Offset } from '../utils/text/baseTextParser/baseTextParser';
import { getCachedTokenSize } from '../utils/text/tokenMeasureCache/tokenMeasureCache';

import { _parseBreak, BreakElementType, TextBreak } from './textBreak';
import {
  _parseEllipsis,
  EllipsisElementType,
  TextEllipsis,
} from './textEllipsis';
import { textStyles } from './textStyles';

interface Props extends PropsWithChildren<ReactElement | ReactElement[]> {
  lineGap?: number;
  maxLines?: number;
  width?: number;
  style?: Partial<IBitmapTextStyle>;
  offset?: Offset;
}

export function TextContent(props: Props): JSX.Element {
  const {
    children,
    maxLines = 10,
    lineGap = 0,
    width = nodeStyles._content.width,
    style = textStyles.normal,
    offset: initOffset = { x: 0, y: 0 },
  } = props;

  const text = useMemo(() => {
    if (!children) {
      return [];
    }

    const state = { lines: 0 };
    const offset: Offset = { ...initOffset };

    const parseChildren = (
      child: ReactElement,
      index: number,
    ): ReactElement => {
      if (!child || state.lines >= maxLines) {
        return createElement(Fragment);
      }

      if (child.type === Fragment) {
        const {
          props: { children },
        } = child;

        return cloneElement(
          child,
          { key: index },
          Children.map(children, parseChildren),
        );
      }

      const { props } = child;
      const { offset: childOffset } = props as BaseElementProps;

      const singleSpaceWidth = Math.max(
        getCachedTokenSize(' ', props).width | 0,
        4,
      );
      const baseParserProps = {
        singleSpaceWidth,
        width,
        style,
      };

      offset.x += childOffset?.x || 0;
      offset.y += childOffset?.y || 0;

      if (child.type === TextEllipsis) {
        const { children, height, lines } = _parseEllipsis(
          child as EllipsisElementType,
          {
            ...baseParserProps,
            offset,
          },
        );

        offset.y = height + lineGap;
        state.lines += lines;

        return createElement(Fragment, null, children);
      }

      if (child.type === TextBreak) {
        const { props } = child;
        const { lineGap: childLineGap = lineGap } = props;
        const { children, height, lines } = _parseBreak(
          child as BreakElementType,
          {
            maxLines: maxLines - state.lines,
            lineGap: childLineGap,
            ...baseParserProps,
            offset,
          },
        );

        offset.y = height + childLineGap;
        state.lines += lines;

        return createElement(Fragment, null, children);
      }

      return cloneElement(child);
    };

    const childrenWithOffset = Children.map<ReactElement, ReactElement>(
      children,
      parseChildren,
    );

    return childrenWithOffset;
  }, [style, children, width, lineGap, maxLines, initOffset]);

  return <>{text}</>;
}
